import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { siteConfig } from '../data/mock';
import LanguageSwitcher from './LanguageSwitcher';
import { isEnglishPath } from '../data/i18nMap';
import { EN_NAV } from '../data/englishPages';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isEn = isEnglishPath(location.pathname);

  const isActive = (path) => {
    if (path === '/' || path === '/en/') return location.pathname === path || (path === '/en/' && location.pathname === '/en');
    return location.pathname.startsWith(path);
  };

  const frNavigation = [
    { name: 'Accueil', path: '/' },
    { name: 'Prestations', path: '/services' },
    { name: 'Tarifs', path: '/tarifs' },
    { name: 'Zones', path: '/zone' },
    { name: 'Mon parcours', path: '/mon-parcours' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ];

  const navigation = isEn ? EN_NAV : frNavigation;
  const homeLink = isEn ? '/en/' : '/';
  const ctaLink = isEn ? '/en/contact' : '/devis';
  const ctaLabel = isEn ? 'Get a quote' : 'Devis express';
  const badgeLabel = isEn ? '50% tax credit' : "Crédit d'impôt 50%";

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      {/* Top Bar */}
      <div className="bg-emerald-800 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href={`tel:${siteConfig.contact.phone}`} className="flex items-center gap-2 hover:text-lime-300 transition-colors">
              <Phone size={16} />
              <span className="font-medium">{siteConfig.contact.phone}</span>
            </a>
            <a 
              href={`https://wa.me/${siteConfig.contact.whatsapp}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 hover:text-lime-300 transition-colors"
            >
              <MessageCircle size={16} />
              <span>WhatsApp</span>
            </a>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <span className="text-xs sm:text-sm font-semibold bg-lime-500 text-emerald-900 px-2 py-0.5 rounded">
              {badgeLabel}
            </span>
            <LanguageSwitcher className="text-white" />
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between py-3">
          {/* Logo */}
          <Link to={homeLink} className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_45bfe101-04b8-422c-9314-86de491da150/artifacts/hp6ix0ei_fulllogo_transparent_nobuffer.png" 
              alt="Le Rosebif - Jardinier Vendée" 
              className="h-14 w-auto"
              width="200"
              height="56"
              fetchPriority="high"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive(item.path) 
                    ? 'text-emerald-700 bg-emerald-50' 
                    : 'text-gray-700 hover:text-emerald-700 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to={ctaLink}>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5">
                {ctaLabel}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col gap-2">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                    isActive(item.path) 
                      ? 'text-emerald-700 bg-emerald-50' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-200 mt-2">
                <Link to={ctaLink} onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                    {ctaLabel}
                  </Button>
                </Link>
                <a 
                  href={`https://wa.me/${siteConfig.contact.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-3"
                >
                  <Button variant="outline" className="w-full border-green-500 text-green-600 hover:bg-green-50">
                    <MessageCircle size={18} className="mr-2" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
